import { useMemo } from 'react';
import type { ComboboxOnChangeType } from '@zillow/constellation-10';
import { Box, Combobox, FormHelp, Icon, IconButton } from '@zillow/constellation-10';
import { IconClockFilled, IconCloseFilled, IconMinusFilled, IconPlusFilled } from '@zillow/constellation-icons';
import { TourDuration } from '@zg-rentals/ts-z-calendar-client';

import { convert12HourTimeTo24HourTime, createAvailabilityTimeOptions } from '../../../utils/availabilityTimeUtils';
import type { TimeSlot } from '../../../types/TourAvailabilityCards';
import { formatTimeString } from '../../../utils/formatTime';

interface TourAvailabilityCardTimeSlotProps {
  timeSlot: TimeSlot;
  onAddTimeSlot: () => void;
  onChangeTimeSlot: (timeSlot: TimeSlot, field: 'start' | 'end') => ComboboxOnChangeType<string>;
  onRemoveTimeSlot: () => void;
  isTimeSlotRemovalDisabled?: boolean;
  tourDuration?: TourDuration;
}

const TourAvailabilityCardTimeSlot = ({
  timeSlot,
  onAddTimeSlot,
  onChangeTimeSlot,
  onRemoveTimeSlot,
  isTimeSlotRemovalDisabled,
  tourDuration = TourDuration.MINUTES_30,
}: TourAvailabilityCardTimeSlotProps) => {
  const startAvailabilityTimeOptions = useMemo(() => createAvailabilityTimeOptions(tourDuration), [tourDuration]);
  const endAvailabilityTimeOptions = useMemo(() => {
    const selectedStartTime = timeSlot.start ? formatTimeString(timeSlot.start, { showMeridiem: true }) : '';
    return createAvailabilityTimeOptions(tourDuration, convert12HourTimeTo24HourTime(selectedStartTime));
  }, [tourDuration, timeSlot.start]);
  const hasError = Boolean(timeSlot.errorMessage);

  return (
    <div>
      <Box css={{ display: 'flex', gap: '8px', alignItems: 'center' }} data-testid="TourAvailabilityCardTimeSlot">
        <Combobox
          options={startAvailabilityTimeOptions}
          value={timeSlot.start ? formatTimeString(timeSlot.start, { showMeridiem: true }) : ''}
          onChange={onChangeTimeSlot(timeSlot, 'start')}
          renderClear={() => null}
          renderAdornment={(adornmentProps) => {
            const { ref, ...adornmentPropsWithoutRef } = adornmentProps;
            return (
              <Combobox.Adornment {...adornmentPropsWithoutRef}>
                <Icon render={<IconClockFilled />} />
              </Combobox.Adornment>
            );
          }}
          data-testid="AvailabilityEventEditPopoverBody-StartTime-Input"
          css={{ flexGrow: '1' }}
          size="md"
          error={hasError}
          editable={false}
        />
        <Icon size="sm">
          <IconMinusFilled />
        </Icon>
        <Combobox
          options={endAvailabilityTimeOptions}
          value={timeSlot.end ? formatTimeString(timeSlot.end, { showMeridiem: true }) : ''}
          onChange={onChangeTimeSlot(timeSlot, 'end')}
          renderClear={() => null}
          renderAdornment={(adornmentProps) => {
            const { ref, ...adornmentPropsWithoutRef } = adornmentProps;
            return (
              <Combobox.Adornment {...adornmentPropsWithoutRef}>
                <Icon render={<IconClockFilled />} />
              </Combobox.Adornment>
            );
          }}
          data-testid="TourAvailabilityCardTimeSlot-StartTime-Input"
          css={{ flexGrow: '1' }}
          size="md"
          error={hasError}
          editable={false}
        />
        <IconButton
          emphasis="bare"
          shape="circle"
          size="sm"
          title="Remove"
          tone="neutral"
          onClick={onRemoveTimeSlot}
          disabled={isTimeSlotRemovalDisabled}
          data-testid="TourAvailabilityCardTimeSlot-RemoveTimeSlotButton"
        >
          <Icon>
            <IconCloseFilled />
          </Icon>
        </IconButton>
        <IconButton
          emphasis="bare"
          shape="circle"
          size="sm"
          title="Add time slot"
          tone="neutral"
          onClick={onAddTimeSlot}
          data-testid="TourAvailabilityCardTimeSlot-AddTimeSlotButton"
        >
          <Icon>
            <IconPlusFilled />
          </Icon>
        </IconButton>
      </Box>
      {hasError && (
        <FormHelp error css={{ marginTop: 'xs' }} data-testid="TourAvailabilityCardTimeSlot-ErrorMessage">
          {timeSlot.errorMessage}
        </FormHelp>
      )}
    </div>
  );
};

export default TourAvailabilityCardTimeSlot;
